import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { PropertyKitItem, ItemFormData, PropertyKitControlType } from './types';

const QUERY_KEY = ['property-kit-items'];

interface UsePropertyKitItemsOptions {
  categoryId?: string;
  controlType?: PropertyKitControlType;
  search?: string;
}

interface ReorderInput {
  itemId: string;
  newSortOrder: number;
  swapItemId: string;
  swapSortOrder: number;
}

export function usePropertyKitItems(options: UsePropertyKitItemsOptions = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { categoryId, controlType, search } = options;

  // Fetch items with filters
  const query = useQuery({
    queryKey: [...QUERY_KEY, categoryId, controlType, search],
    queryFn: async (): Promise<PropertyKitItem[]> => {
      let queryBuilder = supabase
        .from('property_kit_items')
        .select(`
          *,
          category:property_kit_categories(*)
        `)
        .order('category_id', { ascending: true })
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (categoryId) {
        queryBuilder = queryBuilder.eq('category_id', categoryId);
      }

      if (controlType) {
        queryBuilder = queryBuilder.eq('control_type', controlType);
      }

      if (search) {
        queryBuilder = queryBuilder.or(`name.ilike.%${search}%,description.ilike.%${search}%`);
      }

      const { data, error } = await queryBuilder;

      if (error) throw error;
      return data as PropertyKitItem[];
    },
  });

  // Fetch single item - memoized to prevent unnecessary re-renders
  const getItem = useCallback(async (id: string): Promise<PropertyKitItem | null> => {
    const { data, error } = await supabase
      .from('property_kit_items')
      .select(`
        *,
        category:property_kit_categories(*)
      `)
      .eq('id', id)
      .single();

    if (error) return null;
    return data as PropertyKitItem;
  }, []);

  // Create item
  const createMutation = useMutation({
    mutationFn: async (input: ItemFormData) => {
      // Get max sort_order for this category to auto-assign
      const { data: existing } = await supabase
        .from('property_kit_items')
        .select('sort_order')
        .eq('category_id', input.category_id)
        .order('sort_order', { ascending: false })
        .limit(1);

      const maxSortOrder = existing?.[0]?.sort_order ?? -1;

      const { data, error } = await supabase
        .from('property_kit_items')
        .insert({
          category_id: input.category_id,
          name: input.name,
          control_type: input.control_type,
          ideal_quantity: input.ideal_quantity,
          description: input.description,
          image_url: input.image_url,
          sort_order: maxSortOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Artículo creado',
        description: 'El artículo se ha creado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo crear el artículo: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Update item
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...input }: ItemFormData & { id: string }) => {
      const { data, error } = await supabase
        .from('property_kit_items')
        .update({
          category_id: input.category_id,
          name: input.name,
          control_type: input.control_type,
          ideal_quantity: input.ideal_quantity,
          description: input.description,
          image_url: input.image_url,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Artículo actualizado',
        description: 'El artículo se ha actualizado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo actualizar el artículo: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Reorder items within category (optimistic update)
  const reorderMutation = useMutation({
    mutationFn: async ({
      itemId,
      newSortOrder,
      swapItemId,
      swapSortOrder,
    }: ReorderInput) => {
      const [result1, result2] = await Promise.all([
        supabase
          .from('property_kit_items')
          .update({ sort_order: newSortOrder })
          .eq('id', itemId),
        supabase
          .from('property_kit_items')
          .update({ sort_order: swapSortOrder })
          .eq('id', swapItemId),
      ]);

      if (result1.error) throw result1.error;
      if (result2.error) throw result2.error;
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });

      // Snapshot the previous value
      const previousItems = queryClient.getQueryData<PropertyKitItem[]>([...QUERY_KEY, categoryId, controlType, search]);

      // Optimistically update to the new value - swap positions AND sort_order values
      queryClient.setQueryData<PropertyKitItem[]>(
        [...QUERY_KEY, categoryId, controlType, search],
        (old) => {
          if (!old) return old;
          
          return old.map((item) => {
            if (item.id === variables.itemId) {
              return { ...item, sort_order: variables.newSortOrder };
            }
            if (item.id === variables.swapItemId) {
              return { ...item, sort_order: variables.swapSortOrder };
            }
            return item;
          }).sort((a, b) => a.sort_order - b.sort_order);
        }
      );

      return { previousItems };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousItems) {
        queryClient.setQueryData(
          [...QUERY_KEY, categoryId, controlType, search],
          context.previousItems
        );
      }
      toast({
        title: 'Error',
        description: `No se pudo reordenar: ${err instanceof Error ? err.message : 'Error desconocido'}`,
        variant: 'destructive',
      });
    },
    onSuccess: () => {
      // Force immediate refetch to sync with DB
      queryClient.refetchQueries({ 
        queryKey: [...QUERY_KEY, categoryId, controlType, search],
        type: 'active',
      });
    },
  });

  // Delete item
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('property_kit_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Artículo eliminado',
        description: 'El artículo se ha eliminado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo eliminar el artículo. Verifica que no esté en uso en kits.`,
        variant: 'destructive',
      });
    },
  });

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    getItem,
    createItem: createMutation.mutateAsync,
    updateItem: updateMutation.mutateAsync,
    deleteItem: deleteMutation.mutateAsync,
    reorderItem: reorderMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
  };
}
